import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface MultiSelectOption {
  value: string;
  label: string;
  count?: number;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionToggle = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const getSelectedLabels = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const option = options.find((opt) => opt.value === selected[0]);
      return option?.label || selected[0];
    }
    return `${selected.length} lists selected`;
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full justify-between text-left font-normal",
          "border-misty-sage text-forest-green hover:bg-forest-green/5",
          !selected.length && "text-forest-green/50"
        )}
      >
        <span className="truncate">{getSelectedLabels()}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 opacity-50 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </Button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-misty-sage rounded-md shadow-lg">
          <div className="p-2 space-y-1 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <div
                key={option.value}
                className={cn(
                  "flex items-center space-x-3 p-2 rounded-sm cursor-pointer hover:bg-forest-green/5 transition-colors",
                  selected.includes(option.value) && "bg-forest-green/10"
                )}
                onClick={() => handleOptionToggle(option.value)}
              >
                <div
                  className={cn(
                    "w-4 h-4 border border-forest-green/30 rounded-sm flex items-center justify-center",
                    selected.includes(option.value) &&
                      "bg-forest-green border-forest-green"
                  )}
                >
                  {selected.includes(option.value) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-forest-green font-medium">
                    {option.label}
                  </span>
                  {option.count !== undefined && (
                    <span className="text-xs text-forest-green/60 ml-2">
                      ({option.count} subscribers)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selected.length > 0 && (
            <div className="border-t border-misty-sage p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange([])}
                className="w-full text-forest-green/70 hover:text-forest-green hover:bg-forest-green/5"
              >
                Clear all
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
